import { type UtilityComponents } from '../AIDecisionLogger';
import { type AIWeights } from '../utils/AIConfig';
import { type BattleUnit, Hex, PathInfo } from '../../../types/battle.types';
import { Goal } from '@/core/battle/ai/strategic/AIGoals';
import { BehaviorStrategy } from '@/core/battle/ai/BehaviorAnalyzer';
import { BattleData } from '@/store/useBattleStore';


export const StrategicStance = {
    AGGRESSIVE: 'AGGRESSIVE',
    BALANCED: 'BALANCED',
    DEFENSIVE: 'DEFENSIVE', 
} as const;

export type StrategicStance = typeof StrategicStance[keyof typeof StrategicStance];

export const AIActionType = {
    Attack: 'attack',
    MoveAttack: 'move_attack',
    Move: 'move',
    Wait: 'wait',
} as const;
export type AIActionType = (typeof AIActionType)[keyof typeof AIActionType];


export interface AIPlan {
    type: AIActionType;
    unitId: string;
    utility: number;
    targetHexId?: string;
    targetPath?: PathInfo;
    targetUnitId?: string;
    skillId?: string;
}

export interface ActionSequence {
    actions: AIPlan[];
    totalUtility: number;
    finalStamina: number;
}

export interface HexDistanceInfo {
    distances: Map<string, number>;
    minDist: number;
    closestEnemyId: string | null;
}

export interface PrecomputedHexData {
    distanceMatrix: Map<string, HexDistanceInfo>;
    oppRiskCache: Map<string, number>;
}

export interface PathsToEnemyData {
    pathHexes: Map<string, PathData>; 
    pathsToEnemies: Map<string, string[]>
}

export interface PathData {
    maxDmg: number;    // Найбільший дамаг, до якого веде цей гекс
    sumDmg: number;    // Сумарний дамаг усіх цілей попереду (потенціал зони)
    enemySumDmg: number;
    enemyMaxDmg: number;
    hasKill: boolean;
    dist: number;   // Найкоротша відстань до ворога через цей гекс
}


// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface PlanningContext {
    unit: BattleUnit;
    goal: Goal | null;
    round: number;

    stance: StrategicStance;
    weights: AIWeights;
    behavior: BehaviorStrategy;
    
    allies: BattleUnit[];
    enemies: BattleUnit[];

    target?: BattleUnit;
    battleMap: Map<string, Hex>;
    reachableMap: Map<string, number>;
    parentMap: Map<string, string | null>;
    threatMap: Map<string, number>;
    zocMap: Map<string, ZocNode>;
    protectionMap: Map<string, number>;
    tacticalMap: TacticalMapData;
    secondaryTacticalMap: TacticalMapData;

    unitAvgDmg: number;
    unitTotalHp: number;
    unitValue: number;
    targetValue: number;

    totalEnemiesValue: number;
    totalAlliesValue: number;

    storeState: BattleData;
}

export interface PlanningState {
    position: { q: number; r: number };
    stamina: number;
    actionsUsed: AIPlan[];
    totalUtility: number;
    depth: number;
}

export interface EvaluatedCandidate {
    utility: number;
    components?: UtilityComponents;
}

export interface ActionCandidate {
    action: AIPlan;
    utility: number;
    terminalScore?: number;
    staminaAfter: number;
    position: { q: number; r: number };
    components?: UtilityComponents;
    metadata?: {
        type: 'path-based' | 'opportunistic' | 'attack' | 'retreat' | 'wait';
        targetEnemy?: string;
        pathAlignment?: boolean;
    };
}

export interface IEvaluator<T> {
    evaluate(state: PlanningState, ctx: PlanningContext, target: T, additionalData?: any): EvaluatedCandidate;
}

export interface IActionGenerator {
    generatorType: AIActionType;
    // Повертає список кандидатів, використовуючи переданий оцінювач
    generate(state: PlanningState, context: PlanningContext, evaluator: IEvaluator<any>): ActionCandidate[];
}

export interface ActionComponent {
    generator: IActionGenerator;
    evaluator: IEvaluator<any>;
}

/**
 * Результат тактичного сканування карти.
 * Ключ — це координати гекса (наприклад, "17:-6").
 */
export interface TacticalMapData {
    // Карта всіх досяжних точок
    nodes: Record<string, TacticalNode>;
    
    // Екстремуми для нормалізації (proximity = 1 - dist/maxDist)
    stats: {
        minDist: number;
        maxDist: number;
    };

    // Групування для швидкого пошуку: який гекс найкращий для підходу до конкретного ворога
    targets: Record<string, {
        nearestHex: string;
        minDist: number;
    }>;
}

export interface TacticalNode {
    dist: number;      // Тактична відстань (стаміна + штрафи)
    directDist?: number;      // Пряма відстань
    moveCost: number;
    targetId: string;  // ID ворога, до якого цей шлях найкоротший
    parentId?: string;
}

export interface ZocNode {
    totalThreat: number;
    attackersCount: number;
    attackersIds: string[];
}

export interface SearchSource {
    id: string;
    q: number;
    r: number;
}